﻿import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
﻿import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTheme } from '@/contexts/ThemeContext';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import type { Agreement, Payment } from '@/lib/database.types';
import { fetchAgreementsByIds, listMyPayments } from '@/lib/agreements';
import { formatAmount, formatDateTime, paymentStatusColor, paymentStatusLabel } from '@/lib/format';
import { ErrorText } from '@/components/ErrorText';

export default function PaymentsScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [agreements, setAgreements] = useState<Record<string, Agreement>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await listMyPayments();
      setPayments(rows);
      setAgreements(await fetchAgreementsByIds(rows.map((p) => p.agreement_id)));
    } catch (e) {
      setError(getErrorMessage(e, t('common.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top + spacing.lg }]}>
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('payments.title')}</Text>
        <View style={styles.spacer} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={payments}
          keyExtractor={(p) => p.id}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
          ListHeaderComponent={
            payments.length > 0 ? (
              <Text style={styles.subtitle}>{t('payments.subtitle')}</Text>
            ) : null
          }
          ListEmptyComponent={
            <View style={styles.center}>
              <Text style={styles.emptyTitle}>{t('payments.empty_title')}</Text>
              <Text style={styles.emptyText}>
                {t('payments.empty_body')}
              </Text>
            </View>
          }
          renderItem={({ item }) => (
            <Pressable
              style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
              onPress={() => router.push({ pathname: '/(app)/payments/[id]', params: { id: item.id } })}
              accessibilityRole="button"
            >
              <View style={styles.cardLeft}>
                <Text style={styles.cardTitle} numberOfLines={1}>
                  {agreements[item.agreement_id]?.title ?? t('payments.agreement_fallback')}
                </Text>
                <Text style={styles.cardDate}>
                  {formatDateTime(item.created_at)}
                  {item.wallet_paid ? t('payments.walletMethod') : ''}
                </Text>
              </View>
              <View style={styles.cardRight}>
                <Text style={styles.cardAmount}>{formatAmount(item.amount, item.currency)}</Text>
                <Text style={[styles.cardStatus, { color: paymentStatusColor(item.status) }]}>
                  {paymentStatusLabel(item.status)}
                </Text>
              </View>
            </Pressable>
          )}
        />
      )}

      <ErrorText>{error}</ErrorText>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      paddingHorizontal: spacing.lg,
    },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.md,
    },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
      width: 70,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    spacer: { width: 70 },
    subtitle: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      marginBottom: spacing.sm,
    },
    list: {
      gap: spacing.md,
      paddingVertical: spacing.sm,
      flexGrow: 1,
    },
    card: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: spacing.md,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
    },
    cardPressed: { opacity: 0.85 },
    cardLeft: {
      flex: 1,
      gap: 2,
    },
    cardTitle: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    cardDate: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    cardRight: {
      alignItems: 'flex-end',
      gap: 2,
    },
    cardAmount: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    cardStatus: {
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.xxl,
    },
    emptyTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '600',
    },
    emptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
      textAlign: 'center',
      paddingVertical: spacing.sm,
    },
  });
}
